import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Button, Paper, CircularProgress, Slide, Alert } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ConfettiExplosion from "react-confetti-explosion";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { userHasActiveEntitlement } from "../utils/entitlements";
import { trackEvent } from "../utils/analytics/gtag";

const RETRY_INTERVAL_MS = 2000;
const RETRY_TOTAL_MS = 20000;

export default function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { country } = useParams();
  const { hydrateFromCurrentUser } = useAuth() || {};

  const [status, setStatus] = useState("processing");
  const [error, setError] = useState("");
  const [exploding, setExploding] = useState(true);
  const retryTimerRef = useRef(null);
  const cancelledRef = useRef(false);
  const premiumActivatedTrackedRef = useRef(false);

  const params = new URLSearchParams(location.search);
  const sessionId = params.get("session_id");
  const planRaw = (params.get("plan") || "").trim().toLowerCase();
  const plan = planRaw === "yearly" ? "yearly" : planRaw === "monthly" ? "monthly" : null;
  const currency = (params.get("currency") || "").trim().toUpperCase() || null;
  const lang = country || "en";
  const intent = (params.get("intent") || "").trim().toLowerCase() === "premium" ? "premium" : null;

  useEffect(() => {
    trackEvent("funnel_payment_success_view", {
      lang,
      country: lang,
      plan,
      currency: null,
      intent,
      user_tier: "free",
    });
  }, [lang, plan, intent]);

  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const waitForNextRetry = () =>
    new Promise((resolve) => {
      retryTimerRef.current = setTimeout(resolve, RETRY_INTERVAL_MS);
    });

  const refreshUntilEntitled = async () => {
    if (!hydrateFromCurrentUser) {
      setStatus("pending");
      setError(t("payment_processing_late") || "We're still processing your payment.");
      return;
    }

    clearRetryTimer();
    setStatus("processing");
    setError("");
    setExploding(true);

    const startedAt = Date.now();
    while (!cancelledRef.current && Date.now() - startedAt <= RETRY_TOTAL_MS) {
      const result = await hydrateFromCurrentUser();
      const currentUser = result?.user || result?.data?.user || null;

      if (result?.ok === false) {
        setError(
          result?.error?.response?.data?.error ||
            result?.error?.message ||
            t("payment_success_error") ||
            "Payment succeeded, but we couldn't refresh your account."
        );
      } else {
        setError("");
      }

      if (userHasActiveEntitlement(currentUser)) {
        if (!premiumActivatedTrackedRef.current) {
          premiumActivatedTrackedRef.current = true;
          trackEvent("subscribe_success", {
            plan: plan || "premium",
            currency,
            billing_period: plan === "yearly" ? "year" : "month",
          });
          trackEvent("funnel_premium_activated", {
            lang,
            country: lang,
            plan,
            currency: null,
            intent,
            user_tier: "premium",
          });
        }
        setStatus("success");
        retryTimerRef.current = setTimeout(() => setExploding(false), 3000);
        return;
      }

      if (Date.now() - startedAt + RETRY_INTERVAL_MS > RETRY_TOTAL_MS) {
        break;
      }
      await waitForNextRetry();
    }

    if (!cancelledRef.current) {
      setStatus("pending");
      setExploding(false);
      setError(
        t("payment_processing_late") ||
          "We're still processing your payment. Please refresh in a moment."
      );
    }
  };

  useEffect(() => {
    cancelledRef.current = false;
    refreshUntilEntitled();
    return () => {
      cancelledRef.current = true;
      clearRetryTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, hydrateFromCurrentUser, t]);

  if (status === "processing") {
    return (
      <Box minHeight="80vh" display="flex" flexDirection="column" gap={2} alignItems="center" justifyContent="center">
        <CircularProgress color="success" />
        <Typography variant="body1">
          {t("payment_processing") || "Processing your payment..."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      minHeight="80vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ background: "radial-gradient(ellipse at center, #e0ffe7 0%, #f8fafb 100%)" }}
    >
      <Slide in direction="up" timeout={650}>
        <Paper
          elevation={6}
          sx={{
            maxWidth: 420,
            mx: "auto",
            p: 4,
            borderRadius: 6,
            textAlign: "center",
            boxShadow: "0 8px 32px 0 rgba(34,139,34,0.09)",
            position: "relative",
            background: "white",
          }}
        >
          {exploding && (
            <Box sx={{ position: "absolute", left: "50%", top: 60, transform: "translateX(-50%)" }}>
              <ConfettiExplosion duration={2500} particleCount={140} />
            </Box>
          )}
          <CheckCircleIcon sx={{ fontSize: 64, color: "#43a047", mb: 1 }} />
          {status === "success" ? (
            <>
              <Typography variant="h4" fontWeight="bold" sx={{ mt: 1, mb: 1 }}>
                {t("payment_success_title") || "Η πληρωμή ολοκληρώθηκε!"}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                {t("payment_success_subtitle") || "Η συνδρομή σου ενεργοποιήθηκε επιτυχώς."}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h4" fontWeight="bold" sx={{ mt: 1, mb: 1 }}>
                {t("payment_processing_late_title") || "Payment received"}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                {t("payment_processing_late") || "We're still processing your payment. Please refresh in a moment."}
              </Typography>
            </>
          )}

          {error && <Alert severity={status === "success" ? "info" : "warning"} sx={{ my: 2 }}>{error}</Alert>}

          {sessionId && (
            <Typography variant="caption" color="text.secondary">
              session_id: {sessionId}
            </Typography>
          )}

          {status === "pending" && (
            <Button sx={{ mt: 1 }} variant="contained" color="primary" onClick={refreshUntilEntitled}>
              {t("refresh_status") || "Refresh status"}
            </Button>
          )}

          <Button
            sx={{ mt: 5 }}
            variant="outlined"
            color="secondary"
            onClick={() => {
              // Recommended: reload page to update subscription in all contexts
              // or navigate to dashboard (which should re-fetch user data!)
              // window.location.reload();
              navigate(`/${country}/client/dashboard`);
            }}
          >
            {t("back_to_dashboard") || "Επιστροφή στο Dashboard"}
          </Button>
        </Paper>
      </Slide>
    </Box>
  );
}
